import { useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import type { OnboardingIllustration } from './types';

const FLOW_IMAGE: Partial<Record<OnboardingIllustration, string[]>> = {
  welcome: [
    '/onboarding-flow/welcome-dashboard.png',
  ],
  revenue: ['/onboarding-flow/total-revenue.svg'],
  welcomeRevenue: ['/assets/revenue.png'],
  performance: ['/assets/performance.png'],
  trophy: ['/assets/trophy.svg'],
  explore: ['/assets/explore.png'],
  fullPicture: ['/assets/clipboard.svg'],
  filteredView: ['/assets/filter.svg'],
  sortResults: ['/assets/updown.svg'],
  seeCategory: ['/assets/category.svg'],
  jumpToItem: ['/assets/search.svg'],
  spend: ['/assets/money.png'],
  restaurant: ['/assets/shop.svg'],
  time: ['/assets/dates.svg'],
  ordersKpis: ['/assets/monitor.png'],
  ordersSupplier: ['/assets/supplier.svg'],
  ordersCategory: ['/assets/pie.svg'],
  ordersSplit: ['/assets/colored_pie.svg'],
  ordersTop: ['/assets/cart.svg'],
  click: ['/assets/click.svg'],
  costCalculator: ['/assets/calculator-icon.svg'],
  costIngredients: ['/assets/ingredients-icon.svg'],
  costSoup: ['/assets/soup.svg'],
  costAb: ['/assets/ab.svg'],
  costCreateDish: ['/assets/click.svg'],
  costOrdered: ['/assets/ingredients-icon.svg'],
  costFind: ['/assets/search.svg'],
  recipeBook: ['/assets/recipe-book.svg'],
  normalBook: ['/assets/normal-book.svg'],
  pencil: ['/assets/pencil.svg'],
  basket: ['/assets/basket.svg'],
  butter: ['/assets/butter.svg'],
  check: ['/assets/check.svg'],
  recipeBook2: ['/assets/recipe-boook2.svg'],
  bin: ['/assets/bin.svg'],
  welcomeInventory: ['/assets/inventory-wel.png', '/assets/inventory-welcome.png'],
  completeInventory: ['/assets/inventory-end.png', '/assets/inventory-welcome.png'],
  welcomeLeakage: ['/assets/leakage.png'],
  leakageJars: ['/assets/jars.png'],
  dropping: ['/assets/dropping.svg'],
  reverseArrow: ['/assets/reverse-arrow.svg'],
  completeLeakage: ['/assets/leakage-end.png'],
  inventoryClipboard: ['/assets/clipboard.svg'],
  inventoryPie: ['/assets/pie.svg'],
  inventoryCountBox: ['/assets/vegs.png', '/assets/boxes.svg'],
  inventoryColors: ['/assets/sliced-pie.svg', '/assets/colored_pie.svg'],
  inventoryLink: ['/assets/Link.svg', '/assets/link.svg'],
  inventoryAb: ['/assets/ab.svg'],
  inventoryWarning: ['/assets/alert.svg'],
  inventoryBars: ['/assets/leaderboard.svg'],
  inventoryBottles: ['/assets/drinks.png'],
  inventoryScale: ['/assets/seasaw-scale.svg'],
  welcomeUtilization: ['/assets/manage.png'],
  utilizationTasks: ['/assets/tasks.png'],
  windows: ['/assets/windows.svg'],
  target: ['/assets/arrow.svg'],
  settings2: ['/assets/settings-2.svg'],
  fruitBasket: ['/assets/fruit-basket.svg'],
  boxes: ['/assets/boxes.svg'],
  file: ['/assets/file.png', '/assets/file.svg'],
  orangeStar: ['/assets/orange-star.svg'],
  link: ['/assets/Link.svg', '/assets/link.svg'],
  weightScale: ['/assets/weight-scale.png'],
};

function FlowImage({ srcs, fallback }: { srcs: string[]; fallback: ReactNode }) {
  const [index, setIndex] = useState(0);
  if (index >= srcs.length) return <>{fallback}</>;
  return (
    <img
      src={srcs[index]}
      alt=""
      className="block h-full w-full object-contain"
      onError={() => setIndex(i => i + 1)}
    />
  );
}

/**
 * Scenes are drawn without a background of their own so they compose onto whatever
 * surface the card gives them — the peach banner in light mode, a dark panel in dark.
 */
const svg = {
  className: 'w-full h-full',
  viewBox: '0 0 320 180',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
  'aria-hidden': true as const,
};

function WelcomeScene() {
  return (
    <svg {...svg} viewBox="0 0 320 200">
      <ellipse cx="160" cy="178" rx="92" ry="10" fill="#0E8965" opacity="0.12" />
      {/* clipboard */}
      <g transform="translate(28 36) rotate(-12)">
        <rect x="0" y="10" width="78" height="96" rx="10" fill="#fff" stroke="#D0D5DD" />
        <rect x="22" y="0" width="34" height="18" rx="6" fill="#F2F4F7" stroke="#D0D5DD" />
        <rect x="12" y="32" width="54" height="8" rx="4" fill="#E4E7EC" />
        <rect x="12" y="48" width="40" height="8" rx="4" fill="#E4E7EC" />
        <circle cx="18" cy="72" r="6" fill="#0E8965" />
        <path d="M15 72 l2.5 2.5 5-5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="18" cy="90" r="6" fill="#D0D5DD" />
      </g>
      {/* bowl */}
      <g transform="translate(118 88)">
        <ellipse cx="42" cy="38" rx="42" ry="14" fill="#B54708" />
        <path d="M8 28 C12 52 72 52 76 28" fill="#DC6803" />
        <ellipse cx="42" cy="26" rx="36" ry="12" fill="#FEC84B" />
        <circle cx="30" cy="24" r="7" fill="#12B76A" />
        <circle cx="50" cy="22" r="8" fill="#F97066" />
        <circle cx="42" cy="30" r="6" fill="#32D583" />
      </g>
      {/* cloud logo */}
      <g transform="translate(118 18)">
        <ellipse cx="42" cy="28" rx="40" ry="26" fill="#0E8965" />
        <ellipse cx="18" cy="34" rx="18" ry="14" fill="#0E8965" />
        <ellipse cx="66" cy="34" rx="18" ry="14" fill="#0E8965" />
        <path d="M34 24 v16 M34 40 l-8 8 M34 40 l8 8" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        <circle cx="50" cy="28" r="2.5" fill="#fff" />
      </g>
      {/* laptop */}
      <g transform="translate(198 78)">
        <rect x="8" y="0" width="96" height="62" rx="8" fill="#344054" />
        <rect x="14" y="6" width="84" height="46" rx="4" fill="#F2F4F7" />
        <path d="M22 40 L40 26 L54 34 L78 16" stroke="#0E8965" strokeWidth="3" strokeLinejoin="round" fill="none" />
        <rect x="0" y="60" width="112" height="10" rx="3" fill="#667085" />
      </g>
      {/* coins */}
      <g transform="translate(232 38)">
        <ellipse cx="16" cy="28" rx="16" ry="6" fill="#F79009" />
        <rect x="0" y="10" width="32" height="18" fill="#FDB022" />
        <ellipse cx="16" cy="10" rx="16" ry="6" fill="#FEDF89" />
        <ellipse cx="16" cy="4" rx="16" ry="6" fill="#F79009" />
      </g>
    </svg>
  );
}

function RevenueScene() {
  return (
    <svg {...svg}>
      <rect width="320" height="180" fill="#FDF2E9" />
      <g transform="translate(108 36)">
        <rect x="8" y="28" width="96" height="72" rx="10" fill="#F79009" />
        <rect x="20" y="8" width="72" height="36" rx="8" fill="#DC6803" />
        <text x="56" y="32" textAnchor="middle" fill="#fff" fontSize="22" fontWeight="700" fontFamily="sans-serif">$</text>
        <rect x="28" y="48" width="14" height="36" rx="3" fill="#FEDF89" />
        <rect x="48" y="40" width="14" height="44" rx="3" fill="#fff" />
        <rect x="68" y="52" width="14" height="32" rx="3" fill="#FEDF89" />
        <rect x="20" y="92" width="56" height="8" rx="2" fill="#FEF0C7" />
      </g>
    </svg>
  );
}

function SpendScene() {
  return (
    <svg {...svg}>
      <rect width="320" height="180" fill="#ECFDF3" />
      <g transform="translate(70 40)">
        <path d="M70 8 C110 8 128 40 128 72 C128 112 96 140 70 148 C44 140 12 112 12 72 C12 40 30 8 70 8Z" fill="#0E8965" />
        <text x="70" y="88" textAnchor="middle" fill="#fff" fontSize="42" fontWeight="700" fontFamily="sans-serif">$</text>
      </g>
      <g transform="translate(196 78)">
        <ellipse cx="22" cy="48" rx="22" ry="8" fill="#F79009" />
        <rect x="0" y="24" width="44" height="24" fill="#FDB022" />
        <ellipse cx="22" cy="24" rx="22" ry="8" fill="#FEDF89" />
        <ellipse cx="22" cy="16" rx="22" ry="8" fill="#F79009" />
        <ellipse cx="22" cy="8" rx="22" ry="8" fill="#FEDF89" />
      </g>
    </svg>
  );
}

function CostScene() {
  return (
    <svg {...svg}>
      <g transform="translate(100 28)">
        <circle cx="60" cy="64" r="54" fill="#F2F4F7" />
        <path d="M60 64 L60 10 A54 54 0 0 1 108 86 Z" fill="#F04438" />
        <path d="M60 64 L108 86 A54 54 0 0 1 28 108 Z" fill="#FDB022" />
        <path d="M60 64 L28 108 A54 54 0 0 1 16 50 Z" fill="#2E90FA" />
        <path d="M60 64 L16 50 A54 54 0 0 1 60 10 Z" fill="#F79009" />
        <circle cx="60" cy="64" r="18" fill="#fff" />
      </g>
    </svg>
  );
}

function RestaurantScene() {
  return (
    <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none" aria-hidden>
      <rect x="8" y="26" width="48" height="28" rx="4" fill="#0E8965" />
      <path d="M6 28 L32 10 L58 28" fill="#155EEF" />
      <rect x="26" y="36" width="12" height="18" rx="2" fill="#FEF0C7" />
      <rect x="14" y="34" width="8" height="8" rx="1" fill="#D1E9FF" />
      <rect x="42" y="34" width="8" height="8" rx="1" fill="#D1E9FF" />
      <circle cx="48" cy="18" r="6" fill="#FDB022" />
    </svg>
  );
}

function CategoryScene() {
  return (
    <svg {...svg} viewBox="0 0 320 180">
      <g transform="translate(48 44)">
        <rect x="70" y="48" width="52" height="70" rx="10" fill="#F79009" />
        <rect x="78" y="38" width="36" height="18" rx="6" fill="#DC6803" />
        <circle cx="96" cy="22" r="10" fill="#F04438" />
        <rect x="88" y="0" width="16" height="18" rx="4" fill="#F04438" />
      </g>
      <g transform="translate(168 70)">
        <rect x="0" y="20" width="70" height="52" rx="12" fill="#FDB022" />
        <rect x="8" y="8" width="54" height="24" rx="8" fill="#DC6803" />
        <rect x="24" y="0" width="22" height="14" rx="4" fill="#F79009" />
      </g>
      <g transform="translate(40 78)">
        <ellipse cx="36" cy="48" rx="36" ry="14" fill="#B54708" />
        <path d="M8 40 C12 68 60 68 64 40" fill="#DC6803" />
        <ellipse cx="36" cy="38" rx="30" ry="10" fill="#12B76A" />
      </g>
    </svg>
  );
}

function TimeScene() {
  return (
    <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none" aria-hidden>
      <rect x="8" y="12" width="48" height="44" rx="8" fill="#EFF8FF" stroke="#84CAFF" strokeWidth="2" />
      <rect x="8" y="12" width="48" height="14" rx="8" fill="#0E8965" />
      <rect x="18" y="8" width="6" height="10" rx="2" fill="#667085" />
      <rect x="40" y="8" width="6" height="10" rx="2" fill="#667085" />
      <rect x="16" y="32" width="10" height="8" rx="2" fill="#0E8965" />
      <rect x="30" y="32" width="10" height="8" rx="2" fill="#D0D5DD" />
      <rect x="44" y="32" width="10" height="8" rx="2" fill="#D0D5DD" />
      <rect x="16" y="44" width="10" height="8" rx="2" fill="#D0D5DD" />
      <rect x="30" y="44" width="10" height="8" rx="2" fill="#0E8965" />
    </svg>
  );
}

function OrdersScene() {
  return (
    <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none" aria-hidden>
      <rect x="12" y="8" width="40" height="48" rx="6" fill="#fff" stroke="#D0D5DD" strokeWidth="2" />
      <rect x="20" y="16" width="24" height="4" rx="2" fill="#0E8965" />
      <rect x="20" y="26" width="18" height="4" rx="2" fill="#D0D5DD" />
      <rect x="20" y="34" width="22" height="4" rx="2" fill="#D0D5DD" />
      <rect x="20" y="42" width="14" height="4" rx="2" fill="#D0D5DD" />
    </svg>
  );
}

function RevenuePageScene() {
  return (
    <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none" aria-hidden>
      <rect x="8" y="12" width="48" height="40" rx="8" fill="#ECFDF3" />
      <path d="M16 40 L26 28 L34 34 L48 18" stroke="#0E8965" strokeWidth="3" strokeLinejoin="round" fill="none" />
      <circle cx="48" cy="18" r="4" fill="#12B76A" />
    </svg>
  );
}

function WelcomeRevenueScene() {
  return (
    <svg {...svg} viewBox="0 0 320 200" className="h-full w-full" style={{ background: '#FFF1E8' }}>
      <rect width="320" height="200" fill="#FFF1E8" />
      {/* salmon plate */}
      <g transform="translate(18 118)">
        <ellipse cx="48" cy="32" rx="48" ry="14" fill="#D0D5DD" />
        <ellipse cx="48" cy="26" rx="40" ry="12" fill="#F2F4F7" />
        <ellipse cx="40" cy="22" rx="22" ry="8" fill="#F97066" />
        <ellipse cx="36" cy="20" rx="10" ry="4" fill="#FDA29B" />
        <rect x="58" y="16" width="18" height="5" rx="2" fill="#12B76A" transform="rotate(-18 67 18)" />
        <rect x="62" y="22" width="16" height="4" rx="2" fill="#039855" transform="rotate(12 70 24)" />
      </g>
      {/* clipboard */}
      <g transform="translate(28 28) rotate(-10)">
        <rect x="0" y="10" width="72" height="92" rx="8" fill="#fff" stroke="#D0D5DD" />
        <rect x="20" y="0" width="32" height="16" rx="5" fill="#E4E7EC" />
        <rect x="12" y="32" width="48" height="6" rx="3" fill="#E4E7EC" />
        <rect x="12" y="46" width="36" height="6" rx="3" fill="#E4E7EC" />
        <circle cx="18" cy="68" r="6" fill="#0E8965" />
        <path d="M15 68 l2.5 2.5 5-5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="18" cy="86" r="6" fill="#D0D5DD" />
      </g>
      {/* tablet */}
      <g transform="translate(96 36)">
        <rect x="0" y="0" width="88" height="64" rx="8" fill="#1D2939" />
        <rect x="6" y="6" width="76" height="52" rx="4" fill="#F2F4F7" />
        <rect x="12" y="36" width="10" height="14" rx="2" fill="#0E8965" />
        <rect x="26" y="28" width="10" height="22" rx="2" fill="#2E90FA" />
        <rect x="40" y="32" width="10" height="18" rx="2" fill="#F79009" />
        <path d="M56 42 L64 30 L72 36 L80 22" stroke="#7F56D9" strokeWidth="2.4" fill="none" />
      </g>
      {/* laptop */}
      <g transform="translate(186 72)">
        <rect x="8" y="0" width="100" height="62" rx="8" fill="#344054" />
        <rect x="14" y="6" width="88" height="46" rx="4" fill="#F2F4F7" />
        <path d="M24 40 L40 26 L52 34 L78 16" stroke="#0E8965" strokeWidth="3" fill="none" strokeLinejoin="round" />
        <rect x="0" y="60" width="116" height="10" rx="3" fill="#667085" />
      </g>
      {/* coin stacks */}
      <g transform="translate(214 28)">
        <ellipse cx="16" cy="30" rx="16" ry="6" fill="#F79009" />
        <rect x="0" y="12" width="32" height="18" fill="#FDB022" />
        <ellipse cx="16" cy="12" rx="16" ry="6" fill="#FEDF89" />
        <ellipse cx="16" cy="6" rx="16" ry="6" fill="#F79009" />
      </g>
      <g transform="translate(248 40)">
        <ellipse cx="12" cy="22" rx="12" ry="5" fill="#DC6803" />
        <rect x="0" y="10" width="24" height="12" fill="#F79009" />
        <ellipse cx="12" cy="10" rx="12" ry="5" fill="#FDB022" />
      </g>
    </svg>
  );
}

function PerformanceScene() {
  return (
    <svg {...svg} viewBox="0 0 320 180">
      <rect width="320" height="180" fill="#FDF2E9" />
      <g transform="translate(86 22)">
        <rect x="8" y="8" width="132" height="96" rx="10" fill="#1D2939" />
        <rect x="16" y="16" width="116" height="72" rx="4" fill="#F8FAFC" />
        <circle cx="48" cy="48" r="18" fill="#F2F4F7" />
        <path d="M48 48 L48 30 A18 18 0 0 1 64 54 Z" fill="#12B76A" />
        <path d="M48 48 L64 54 A18 18 0 0 1 36 62 Z" fill="#F04438" />
        <rect x="78" y="30" width="10" height="36" rx="2" fill="#0E8965" />
        <rect x="92" y="38" width="10" height="28" rx="2" fill="#2E90FA" />
        <rect x="106" y="24" width="10" height="42" rx="2" fill="#F79009" />
        <path d="M78 22 L90 18 L104 26 L118 14" stroke="#7F56D9" strokeWidth="2" fill="none" />
        <rect x="0" y="104" width="148" height="10" rx="3" fill="#667085" />
        <rect x="54" y="112" width="40" height="6" rx="2" fill="#98A2B3" />
      </g>
    </svg>
  );
}

function TrophyScene() {
  return (
    <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none" aria-hidden>
      <ellipse cx="32" cy="58" rx="16" ry="4" fill="#FEDF89" />
      <rect x="26" y="42" width="12" height="10" rx="2" fill="#DC6803" />
      <path d="M20 44 L44 44 L40 52 L24 52 Z" fill="#F79009" />
      <path d="M18 14 C18 8 46 8 46 14 L44 34 C44 42 20 42 20 34 Z" fill="#FDB022" />
      <path d="M22 16 C22 12 42 12 42 16 L40 32 C40 38 24 38 24 32 Z" fill="#FEDF89" />
      <path d="M18 18 C8 18 8 32 18 32" stroke="#F79009" strokeWidth="3" fill="none" />
      <path d="M46 18 C56 18 56 32 46 32" stroke="#F79009" strokeWidth="3" fill="none" />
      <circle cx="32" cy="24" r="4" fill="#DC6803" />
    </svg>
  );
}

function SeeAllScene() {
  return (
    <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none" aria-hidden>
      <ellipse cx="32" cy="54" rx="18" ry="5" fill="#FEDF89" />
      <rect x="20" y="40" width="24" height="10" rx="4" fill="#DC6803" />
      <circle cx="32" cy="24" r="16" fill="#FDF2E9" stroke="#F79009" strokeWidth="2" />
      <circle cx="32" cy="24" r="7" fill="#F79009" />
      <path d="M32 20 v8 M28 24 h8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function FullPictureScene() {
  return (
    <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none" aria-hidden>
      <rect x="12" y="8" width="40" height="48" rx="6" fill="#fff" stroke="#D0D5DD" strokeWidth="2" />
      <rect x="24" y="4" width="16" height="10" rx="3" fill="#E4E7EC" />
      <circle cx="22" cy="26" r="5" fill="#12B76A" />
      <path d="M19.5 26 l2 2 4-4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="30" y="23" width="16" height="5" rx="2" fill="#E4E7EC" />
      <circle cx="22" cy="40" r="5" fill="#12B76A" />
      <path d="M19.5 40 l2 2 4-4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="30" y="37" width="14" height="5" rx="2" fill="#E4E7EC" />
    </svg>
  );
}

function FilteredViewScene() {
  return (
    <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none" aria-hidden>
      <path d="M14 8 h36 l-12 16 v14 l-12 8 V24 Z" fill="#FDB022" />
      <path d="M18 8 h28 l-10 14 H28 Z" fill="#F79009" />
      <path d="M26 24 v16 l12-8 V24 Z" fill="#DC6803" />
      <rect x="8" y="38" width="48" height="22" rx="6" fill="#fff" stroke="#E4E7EC" />
      <rect x="14" y="46" width="22" height="5" rx="2.5" fill="#D0D5DD" />
      <circle cx="46" cy="49" r="7" fill="#12B76A" />
      <path d="M43 49 l2.2 2.2 4.2-4.4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function WelcomeOrdersScene() {
  return (
    <svg {...svg} viewBox="0 0 320 200">
      <rect width="320" height="200" fill="#F8FAFC" />
      <ellipse cx="160" cy="178" rx="88" ry="10" fill="#0E8965" opacity="0.1" />
      {/* stacked invoices */}
      <g transform="translate(48 44) rotate(-8)">
        <rect x="8" y="12" width="88" height="112" rx="8" fill="#E4E7EC" />
        <rect x="0" y="0" width="88" height="112" rx="8" fill="#fff" stroke="#D0D5DD" />
        <rect x="14" y="16" width="60" height="8" rx="4" fill="#0E8965" />
        <rect x="14" y="32" width="48" height="6" rx="3" fill="#E4E7EC" />
        <rect x="14" y="46" width="56" height="6" rx="3" fill="#E4E7EC" />
        <rect x="14" y="60" width="40" height="6" rx="3" fill="#E4E7EC" />
        <rect x="14" y="84" width="28" height="10" rx="3" fill="#FDB022" />
      </g>
      {/* bowl */}
      <g transform="translate(168 88)">
        <ellipse cx="48" cy="42" rx="48" ry="14" fill="#B54708" />
        <path d="M8 32 C14 62 82 62 88 32" fill="#DC6803" />
        <ellipse cx="48" cy="30" rx="40" ry="12" fill="#FEC84B" />
        <circle cx="36" cy="28" r="8" fill="#12B76A" />
        <circle cx="56" cy="26" r="7" fill="#F97066" />
      </g>
      {/* check stamp */}
      <g transform="translate(228 36)">
        <circle cx="22" cy="22" r="22" fill="#12B76A" />
        <path d="M12 22 l6 6 14-14" stroke="#fff" strokeWidth="4" strokeLinecap="round" fill="none" />
      </g>
    </svg>
  );
}

function CompleteScene() {
  return (
    <svg {...svg} viewBox="0 0 320 200">
      <ellipse cx="160" cy="168" rx="70" ry="14" fill="#D0D5DD" opacity="0.7" />
      <ellipse cx="160" cy="162" rx="64" ry="12" fill="#E4E7EC" />
      {/* laptop */}
      <g transform="translate(96 52)">
        <rect x="8" y="0" width="112" height="78" rx="10" fill="#344054" />
        <rect x="16" y="8" width="96" height="58" rx="4" fill="#F2F4F7" />
        <rect x="24" y="16" width="28" height="16" rx="3" fill="#0E8965" opacity="0.35" />
        <rect x="56" y="16" width="28" height="16" rx="3" fill="#2E90FA" opacity="0.35" />
        <rect x="24" y="38" width="60" height="8" rx="2" fill="#D0D5DD" />
        <rect x="24" y="50" width="44" height="8" rx="2" fill="#E4E7EC" />
        <rect x="0" y="76" width="128" height="12" rx="3" fill="#667085" />
      </g>
      {/* checks */}
      <g transform="translate(48 40)">
        <circle cx="18" cy="18" r="16" fill="#12B76A" />
        <path d="M10 18 l5 5 11-12" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
      </g>
      <g transform="translate(236 28)">
        <circle cx="18" cy="18" r="16" fill="#0E8965" />
        <path d="M10 18 l5 5 11-12" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
      </g>
      {/* plants */}
      <g transform="translate(36 100)">
        <rect x="18" y="40" width="16" height="14" rx="3" fill="#B54708" />
        <path d="M26 40 C10 24 18 8 26 20 C34 8 42 24 26 40" fill="#12B76A" />
      </g>
      <g transform="translate(248 108)">
        <rect x="14" y="32" width="14" height="12" rx="3" fill="#B54708" />
        <path d="M21 32 C10 20 16 6 21 16 C26 6 32 20 21 32" fill="#0E8965" />
      </g>
    </svg>
  );
}

function NotebookScene() {
  return (
    <svg className="h-full w-full" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="12" y="10" width="32" height="40" rx="4" fill="#FEC84B" />
      <rect x="16" y="14" width="24" height="32" rx="2" fill="#FFFCF5" />
      <rect x="19" y="20" width="18" height="3" rx="1.5" fill="#E4E7EC" />
      <rect x="19" y="27" width="14" height="3" rx="1.5" fill="#E4E7EC" />
      <circle cx="40" cy="42" r="10" fill="#12B76A" />
      <path d="M40 37 v10 M35 42 h10" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function SpoonsScene() {
  return (
    <svg className="h-full w-full" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="18" y="8" width="8" height="28" rx="4" fill="#FEC84B" />
      <ellipse cx="22" cy="42" rx="11" ry="9" fill="#F79009" />
      <rect x="33" y="12" width="7" height="24" rx="3.5" fill="#FEC84B" />
      <ellipse cx="36.5" cy="42" rx="9" ry="8" fill="#DC6803" />
    </svg>
  );
}

function BasketPlusScene() {
  return (
    <svg className="h-full w-full" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M12 22 h35 l-4 18 H16 z" fill="#F79009" />
      <path d="M16 22 V16 a6 6 0 0 1 12 0 v6" stroke="#B54708" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <circle cx="42" cy="16" r="11" fill="#12B76A" />
      <path d="M42 10 v12 M36 16 h12" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function CheckScene() {
  return (
    <svg className="h-full w-full" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="29.5" cy="29.5" r="18" fill="#12B76A" />
      <path d="M20 30.5 l6 6 14-14" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WarningScene() {
  return (
    <svg className="h-full w-full" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M29.5 10 L48 44 H11 Z" fill="#F79009" />
      <path d="M29.5 22 v12" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" />
      <circle cx="29.5" cy="39" r="2.2" fill="#fff" />
    </svg>
  );
}

function LeakageWelcomeScene() {
  return (
    <svg className="h-full w-full" viewBox="0 0 280 180" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="38" y="28" width="132" height="88" rx="16" fill="#FEF0C7" />
      <text x="54" y="52" fill="#B54708" fontSize="11" fontWeight="600" fontFamily="sans-serif">Expected</text>
      <text x="54" y="78" fill="#B54708" fontSize="18" fontWeight="700" fontFamily="sans-serif">€8,420</text>
      <rect x="110" y="48" width="132" height="88" rx="16" fill="#DCFAE6" />
      <text x="126" y="72" fill="#039855" fontSize="11" fontWeight="600" fontFamily="sans-serif">Counted</text>
      <text x="126" y="98" fill="#039855" fontSize="18" fontWeight="700" fontFamily="sans-serif">€7,580</text>
      <rect x="86" y="118" width="108" height="36" rx="18" fill="#FEF0C7" stroke="#F79009" strokeWidth="2" />
      <text x="104" y="141" fill="#B54708" fontSize="13" fontWeight="700" fontFamily="sans-serif">€440 Leakage</text>
    </svg>
  );
}

function ChartScene() {
  return (
    <svg className="h-full w-full" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="10" y="32" width="10" height="16" rx="3" fill="#F79009" />
      <rect x="24" y="22" width="10" height="26" rx="3" fill="#DC6803" />
      <rect x="38" y="12" width="10" height="36" rx="3" fill="#F79009" />
    </svg>
  );
}

function BottlesScene() {
  return (
    <svg className="h-full w-full" viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <ellipse cx="160" cy="168" rx="88" ry="8" fill="#F79009" opacity="0.16" />
      <g transform="translate(58 38)">
        <rect x="18" y="0" width="18" height="18" rx="4" fill="#B42318" />
        <rect x="14" y="16" width="26" height="86" rx="10" fill="#D92D20" />
        <rect x="18" y="28" width="18" height="28" rx="4" fill="#F97066" opacity="0.45" />
      </g>
      <g transform="translate(122 28)">
        <rect x="22" y="0" width="16" height="16" rx="4" fill="#DC6803" />
        <rect x="16" y="14" width="28" height="100" rx="10" fill="#F79009" />
        <rect x="20" y="30" width="20" height="8" rx="2" fill="#FEF0C7" />
        <rect x="20" y="46" width="20" height="8" rx="2" fill="#FEF0C7" />
        <rect x="20" y="62" width="20" height="8" rx="2" fill="#FEF0C7" />
      </g>
      <g transform="translate(188 34)">
        <rect x="20" y="0" width="16" height="16" rx="4" fill="#039855" />
        <rect x="14" y="14" width="28" height="96" rx="10" fill="#12B76A" />
        <path d="M20 36 h16" stroke="#ECFDF3" strokeWidth="6" strokeLinecap="round" />
      </g>
      <g transform="translate(78 118)">
        <path d="M8 0 h18 l3 28 H5 Z" fill="#F97066" />
        <ellipse cx="17" cy="0" rx="11" ry="4" fill="#FDA29B" />
      </g>
      <g transform="translate(214 112)">
        <path d="M8 0 h18 l3 32 H5 Z" fill="#A6F4C5" />
        <ellipse cx="17" cy="0" rx="11" ry="4" fill="#DCFAE6" />
      </g>
    </svg>
  );
}

function ScaleScene() {
  return (
    <svg className="h-full w-full" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M29.5 12 v28" stroke="#DC6803" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="29.5" cy="12" r="3.2" fill="#F79009" />
      <path d="M16 18 h27" stroke="#DC6803" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M16 18 L10 30" stroke="#F79009" strokeWidth="2" strokeLinecap="round" />
      <path d="M43 18 L49 30" stroke="#F79009" strokeWidth="2" strokeLinecap="round" />
      <path d="M4 30 h12 a8 6 0 0 1-12 0 Z" fill="#FEC84B" />
      <path d="M43 30 h12 a8 6 0 0 1-12 0 Z" fill="#FEC84B" />
      <rect x="22" y="40" width="15" height="5" rx="1.5" fill="#DC6803" />
      <path d="M18 48 h23" stroke="#B54708" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function TrashScene() {
  return (
    <svg className="h-full w-full" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="29.5" cy="29.5" r="22" fill="#F04438" />
      <path d="M20 24 h19 M24 24 v-4 h11 v4 M23 24 v16 a2 2 0 0 0 2 2 h9 a2 2 0 0 0 2-2 V24" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M27 28 v8 M32 28 v8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

export function OnboardingIllustrationView({
  name,
  compact,
  className,
}: {
  name: OnboardingIllustration;
  compact?: boolean;
  className?: string;
}) {
  const scene = (() => {
    switch (name) {
      case 'welcome':
        return <WelcomeScene />;
      case 'revenue':
        return <RevenueScene />;
      case 'spend':
        return <SpendScene />;
      case 'cost':
        return <CostScene />;
      case 'restaurant':
        return <RestaurantScene />;
      case 'category':
        return compact ? <RestaurantScene /> : <CategoryScene />;
      case 'time':
        return <TimeScene />;
      case 'orders':
        return <OrdersScene />;
      case 'welcomeOrders':
        return <WelcomeOrdersScene />;
      case 'revenuePage':
        return <RevenuePageScene />;
      case 'welcomeRevenue':
        return <WelcomeRevenueScene />;
      case 'performance':
        return <PerformanceScene />;
      case 'trophy':
        return <TrophyScene />;
      case 'seeAll':
        return <SeeAllScene />;
      case 'explore':
        return <SeeAllScene />;
      case 'fullPicture':
        return <FullPictureScene />;
      case 'filteredView':
        return <FilteredViewScene />;
      case 'complete':
        return <CompleteScene />;
      case 'costAddHere':
        return <NotebookScene />;
      case 'costQty':
        return <SpoonsScene />;
      case 'costCart':
        return <BasketPlusScene />;
      case 'costCheck':
      case 'check':
        return <CheckScene />;
      case 'inventoryWarning':
        return <WarningScene />;
      case 'costChart':
      case 'inventoryBars':
        return <ChartScene />;
      case 'inventoryBottles':
        return <BottlesScene />;
      case 'inventoryScale':
        return <ScaleScene />;
      case 'welcomeLeakage':
        return <LeakageWelcomeScene />;
      case 'bin':
        return <TrashScene />;
      default:
        return null;
    }
  })();

  const srcs = FLOW_IMAGE[name];
  if (srcs) {
    // The card owns the box; the image just fills it, so there is one size to change.
    if (compact) {
      return (
        <img
          src={srcs[0]}
          alt=""
          className={cn('block h-[59px] w-[59px] object-contain', className)}
        />
      );
    }
    return <FlowImage srcs={srcs} fallback={scene} />;
  }
  return scene;
}
